import { HttpApiEndpoint, HttpApiGroup } from "@effect/platform";
import * as S from "effect/Schema";
import { LanguageSchema } from "./column-schemas.js";
import { Forbidden } from "./shares.js";
import { PrincipalMiddleware } from "./principal.js";
/** A generic row-not-found outcome, shared by any by-id lookup in this API. */
export const NotFound = S.Struct({ _tag: S.Literal("NotFound") });
/**
 * yp_folders row-shaped. `materialized_path` is already sanitized to the
 * caller's accessible ancestors server-side — never a raw ancestor chain the
 * caller might not have rights to.
 */
export const FolderRow = S.Struct({
    id: S.UUID,
    name: S.String,
    name_t_en: S.NullOr(S.String),
    parent: S.NullOr(S.UUID),
    client_id: S.NullOr(S.UUID),
    depth: S.Int,
    materialized_path: S.String,
    metadata: S.Unknown,
});
/** A folder's ancestor entries for a breadcrumb trail — no depth/path needed. */
export const Breadcrumb = S.Struct({
    id: S.UUID,
    name: S.String,
    name_t_en: S.NullOr(S.String),
    parent: S.NullOr(S.UUID),
    client_id: S.NullOr(S.UUID),
});
export const ViewFoldersParams = S.Struct({
    parent_id: S.optional(S.UUID),
    client_id: S.optional(S.UUID),
    /**
     * PMO-430: narrows the list to the folders below `parent_id` (or, without
     * one, below the shared protected root) whose own name matches — the same
     * term the document list is narrowed by, so the two halves of the document
     * centre answer one search instead of the folder half ignoring it.
     * Containing a matching document never makes a folder a hit.
     */
    search: S.optional(S.String),
    /**
     * Which of a folder's two names `search` is matched against: the one the
     * caller is reading. Without it the German name is matched, matching the
     * product's default locale.
     */
    language: S.optional(LanguageSchema),
});
export const FolderBreadcrumbsParams = S.Struct({
    folder_id: S.UUID,
});
export const ClientDocumentRootParams = S.Struct({
    client_id: S.UUID,
});
export const ClientDocumentRootNotFound = S.Struct({
    _tag: S.Literal("ClientDocumentRootNotFound"),
});
export const CreateClientFolderPayload = S.Struct({
    client_id: S.UUID,
    parent: S.UUID,
    name: S.NonEmptyString,
    name_t_en: S.optional(S.String),
});
export const UpdateClientFolderPayload = S.Struct({
    id: S.UUID,
    name: S.NonEmptyString,
    name_t_en: S.String,
});
export const ClientFolderByIdParams = S.Struct({ id: S.UUID });
export const ClientFolderNotFound = S.Struct({
    _tag: S.Literal("ClientFolderNotFound"),
});
/**
 * Google-Drive-like navigation over a client folder tree (root when
 * `parent_id` is omitted): the client's folders plus whatever is lifted in
 * via `folder_shares`/`file_shares`. Without `client_id` the tree is the
 * caller's own client; a platform role (admin/ohs-admin/sifa/ama, or an
 * external SiFa on an assigned client) may pass `client_id` to browse the
 * client space it is acting in — legacy granted those roles cross-client
 * folder reads.
 *
 * With `search`, one level is replaced by the whole subtree below it, matched
 * on folder name (PMO-430). Visibility is unchanged either way.
 */
export const ViewFolders = HttpApiEndpoint.get("viewFolders", "/folders/view")
    .setUrlParams(ViewFoldersParams)
    .addSuccess(S.Struct({ items: S.Array(FolderRow) }))
    .addError(Forbidden, { status: 403 });
export const FolderBreadcrumbs = HttpApiEndpoint.get("folderBreadcrumbs", "/folders/breadcrumbs")
    .setUrlParams(FolderBreadcrumbsParams)
    .addSuccess(S.Struct({ items: S.Array(Breadcrumb) }))
    .addError(Forbidden, { status: 403 });
/** The structural root of one client's document tree. */
export const ClientDocumentRoot = HttpApiEndpoint.get("clientDocumentRoot", "/folders/client-document-root")
    .setUrlParams(ClientDocumentRootParams)
    .addSuccess(S.Struct({ data: FolderRow }))
    .addError(ClientDocumentRootNotFound, { status: 404 })
    .addError(Forbidden, { status: 403 });
export const CreateClientFolder = HttpApiEndpoint.post("createClientFolder", "/folders/create")
    .setPayload(CreateClientFolderPayload)
    .addSuccess(S.Struct({ data: FolderRow }))
    .addError(Forbidden, { status: 403 });
export const UpdateClientFolder = HttpApiEndpoint.post("updateClientFolder", "/folders/update")
    .setPayload(UpdateClientFolderPayload)
    .addSuccess(S.Struct({ data: FolderRow }))
    .addError(ClientFolderNotFound, { status: 404 })
    .addError(Forbidden, { status: 403 });
export const ClientFolderDeletionImpact = HttpApiEndpoint.get("clientFolderDeletionImpact", "/folders/deletion-impact")
    .setUrlParams(ClientFolderByIdParams)
    .addSuccess(S.Struct({
    data: S.Struct({ files: S.Int, subfolders: S.Int }),
}))
    .addError(ClientFolderNotFound, { status: 404 })
    .addError(Forbidden, { status: 403 });
export const DeleteClientFolder = HttpApiEndpoint.post("deleteClientFolder", "/folders/delete")
    .setPayload(ClientFolderByIdParams)
    .addSuccess(S.Struct({ data: S.Struct({ deleted: S.Literal(true) }) }))
    .addError(ClientFolderNotFound, { status: 404 })
    .addError(Forbidden, { status: 403 });
export const foldersGroup = HttpApiGroup.make("folders")
    .add(ViewFolders)
    .add(FolderBreadcrumbs)
    .add(ClientDocumentRoot)
    .add(CreateClientFolder)
    .add(UpdateClientFolder)
    .add(ClientFolderDeletionImpact)
    .add(DeleteClientFolder)
    .middleware(PrincipalMiddleware);
//# sourceMappingURL=folders.js.map